import { Link } from 'react-router-dom';

const PriceTable = ({ rows, testid }) => (
  <div data-testid={testid} className="rounded-2xl overflow-hidden border-2 border-black bg-white mb-4 shadow-[4px_4px_0_#FF6600]">
    <table className="w-full text-sm">
      <thead className="bg-black text-brand-orange">
        <tr>
          <th className="text-left px-5 py-3 font-display font-black text-[11px] tracking-[0.24em] uppercase">Quantity</th>
          <th className="text-right px-5 py-3 font-display font-black text-[11px] tracking-[0.24em] uppercase">Price / Shirt</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-black/10">
        {rows.map(([qty, price], i) => (
          <tr key={i} className="hover:bg-brand-cream transition-colors">
            <td className="px-5 py-3 font-body text-black">{qty}</td>
            <td className="px-5 py-3 text-right font-display font-black text-brand-orange">{price}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CheckList = ({ items, columns = 2 }) => (
  <ul className={`grid grid-cols-1 sm:grid-cols-${columns} gap-y-2 gap-x-6 list-none font-body text-black/80`}>
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3">
        <span className="mt-1 shrink-0 w-4 h-4 rounded-full bg-brand-orange text-black text-[10px] font-black flex items-center justify-center">✓</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Section = ({ eyebrow, title, children }) => (
  <section className="mb-12">
    {eyebrow && (
      <div className="mb-3 font-display font-black text-[11px] tracking-[0.32em] uppercase text-brand-orange">
        {eyebrow}
      </div>
    )}
    <h2 className="font-display font-black text-2xl sm:text-3xl text-black mb-5 tracking-tight">{title}</h2>
    <div className="font-body text-base text-black/75 leading-relaxed space-y-4">
      {children}
    </div>
  </section>
);

const PricingGuidePage = () => (
  <main data-testid="pricing-guide-page" className="min-h-screen bg-brand-cream px-6 py-14 md:py-20">
    <div className="max-w-3xl mx-auto">
      <Link to="/" className="inline-flex items-center gap-2 font-display font-black text-xs uppercase tracking-[0.24em] text-black/60 hover:text-black mb-8">
        ← Back to Shirt Happens
      </Link>

      <div className="mb-2 font-display font-black text-[11px] tracking-[0.32em] uppercase text-brand-orange">
        Transparent pricing
      </div>
      <h1 className="font-display font-black text-5xl sm:text-6xl leading-[0.95] text-black tracking-tight mb-6">
        Pricing Guide
      </h1>
      <p className="font-body text-lg text-black/70 leading-relaxed mb-3 max-w-2xl">
        <strong>Quality Apparel. Transparent Pricing. Exceptional Service.</strong>
      </p>
      <p className="font-body text-base text-black/70 leading-relaxed mb-12 max-w-2xl">
        Every shirt tells a story. Whether you&rsquo;re promoting your business, outfitting your team, celebrating a special occasion, or creating something completely unique, Shirt Happens Studio delivers apparel you&rsquo;ll be proud to wear. Our pricing is straightforward, our quality is exceptional, and every order is crafted with care.
      </p>

      <Section eyebrow="Standard tees" title="One-Sided Print">
        <p className="text-sm text-black/60">Pricing based on Gildan Soft Style apparel.</p>
        <PriceTable
          testid="pricing-one-sided"
          rows={[
            ['1–10 Shirts', '$25'],
            ['11–20 Shirts', '$24'],
            ['21–35 Shirts', '$23'],
            ['36–50 Shirts', '$22'],
            ['51+ Shirts', 'Contact us for custom pricing'],
          ]}
        />
      </Section>

      <Section eyebrow="Standard tees" title="Front &amp; Back Print">
        <p className="text-sm text-black/60">Pricing based on Gildan Soft Style apparel.</p>
        <PriceTable
          testid="pricing-two-sided"
          rows={[
            ['1–10 Shirts', '$30'],
            ['11–20 Shirts', '$29'],
            ['21–35 Shirts', '$28'],
            ['36–50 Shirts', '$27'],
            ['51+ Shirts', 'Contact us for custom pricing'],
          ]}
        />
      </Section>

      <Section eyebrow="Garments" title="Available Shirt Brands">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <div className="font-display font-black text-black text-sm tracking-[0.18em] uppercase mb-3">Standard pricing</div>
            <CheckList items={['Gildan Soft Style', 'Bella+Canvas', 'A4', 'Laviva Sports']} columns={1} />
          </div>
          <div>
            <div className="font-display font-black text-black text-sm tracking-[0.18em] uppercase mb-3">Premium brand</div>
            <CheckList items={['Comfort Colors* (additional charge applies)']} columns={1} />
          </div>
        </div>
      </Section>

      <Section eyebrow="Sizing" title="Extended Sizes">
        <p>Adult sizes 2XL and larger are <strong>$5 additional per shirt</strong>.</p>
      </Section>

      <Section eyebrow="Design fees" title="Custom Artwork">
        <p>Simple text layouts and minor artwork adjustments are <strong>included</strong> with your order.</p>
        <p>Need a completely custom design? Original custom artwork <strong>starts at $10</strong>, depending on complexity. Any artwork fee will always be discussed and approved before work begins.</p>
      </Section>

      <Section eyebrow="Who we serve" title="Perfect For">
        <CheckList
          items={[
            'Small Businesses', 'Employee Uniforms', 'Schools', 'Churches & Church Groups',
            'Sports Teams', 'Family Reunions', 'Birthday Parties', 'Fundraisers',
            'Community Events', 'Clubs & Organizations', 'Nonprofits', 'Special Events',
          ]}
        />
      </Section>

      <Section eyebrow="What&rsquo;s included" title="The Shirt Happens Studio Difference">
        <p>Every order includes:</p>
        <CheckList
          items={[
            'One Digital Proof for Approval',
            'Premium DTF Printing',
            'Professional Print Setup',
            'Careful Quality Inspection',
            'Fast Turnaround',
            'Friendly, Local Customer Service',
            'Satisfaction-Focused Craftsmanship',
          ]}
        />
      </Section>

      <Section eyebrow="Fine print" title="Important Information">
        <ul className="space-y-2 list-disc pl-5">
          <li>Pricing is based on Gildan Soft Style apparel.</li>
          <li>Comfort Colors garments require an additional charge.</li>
          <li>Adult sizes 2XL and larger are an additional $5 per shirt.</li>
          <li>Texas sales tax (8.25%) will be added where applicable.</li>
          <li>Turnaround times may vary based on order size, garment availability, and artwork approval.</li>
        </ul>
        <p className="pt-2">
          Full terms live on our <Link to="/policies/payment" className="text-brand-orange underline hover:text-black">Payment Policy</Link> page.
        </p>
      </Section>

      <div className="mt-16 p-8 rounded-3xl border-2 border-black bg-white shadow-[8px_8px_0_#FF6600] text-center">
        <div className="font-display font-black text-3xl text-black mb-2">Ready to Make Shirt Happen?</div>
        <p className="font-body text-black/70 mb-6 max-w-xl mx-auto">
          Whether you need one custom shirt or hundreds, we&rsquo;re ready to bring your vision to life.
        </p>
        <Link to="/#custom" className="inline-flex items-center gap-2 font-display font-black text-xs uppercase tracking-[0.24em] px-6 py-3.5 rounded-full bg-black text-brand-orange border-2 border-black hover:bg-brand-orange hover:text-black transition-colors">
          Request your free quote →
        </Link>
        <div className="mt-6 font-display font-black text-brand-orange tracking-tight">
          From Idea to Shirt. We Make Shirt Happen.
        </div>
      </div>
    </div>
  </main>
);

export default PricingGuidePage;
